import isEqual from "lodash.isequal"
import { createActions, createReducer } from "reduxsauce"
import Immutable from "seamless-immutable"

import { preferencesSuccess } from "../User/redux/user.actions"

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
  getAdminUsersRequest: ["payload"], // payload: { params }
  getAdminUsersSuccess: ["payload"], // payload: { data }
  getAdminUsersFailure: ["payload"], // payload: { errors }

  getAdminUsersCountRequest: ["payload"], // payload: { params }
  getAdminUsersCountSuccess: ["payload"], // payload: { data }
  getAdminUsersCountFailure: ["payload"], // payload: { errors }

  getAdminUsersInviteRolesRequest: ["payload"], // payload: { params }
  getAdminUsersInviteRolesSuccess: ["payload"], // payload: { data }
  getAdminUsersInviteRolesFailure: ["payload"], // payload: { errors }

  getAdminUsersExportRequest: ["payload"], // payload: { params }
  getAdminUsersExportSuccess: ["payload"], // payload: { data }
  getAdminUsersExportFailure: ["payload"], // payload: { errors }

  updateAdminUserRoleRequest: ["payload"], // payload: { id, role }
  updateAdminUserRoleSuccess: ["payload"], // payload: { data }
  updateAdminUserRoleFailure: ["payload"], // payload: { errors }

  inviteAdminUserRequest: ["payload"], // payload: { email, role }
  inviteAdminUserSuccess: ["payload"], // payload: { data }
  inviteAdminUserFailure: ["payload"], // payload: { errors }

  resendAdminUserInviteRequest: ["payload"], // payload: id
  resendAdminUserInviteSuccess: ["payload"], // payload: { data }
  resendAdminUserInviteFailure: ["payload"], // payload: { errors }

  deleteAdminUserRequest: ["payload"], // payload: id
  deleteAdminUserSuccess: ["payload"], // payload: { data }
  deleteAdminUserFailure: ["payload"], // payload: { errors }

  adminUsersSetProp: ["key", "value"],
  clearAdminUsersFilters: null,

  logout: null
})

export const AdminUsersTypes = Types
export default Creators

/* ------------- Initial State ------------- */

const columnsAdmin = [
  { field_id: "admin_full_name", label: "Admin full name", active: true },
  { field_id: "email", label: "Admin email", active: true },
  { field_id: "invite_sent", label: "Invite sent", active: true },
  { field_id: "last_2fa_access", label: "Last 2FA access", active: true },
  { field_id: "last_activity", label: "Last activity", active: true },
  { field_id: "role", label: "Role", active: true }
]

export const INITIAL_STATE = Immutable({
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  statistics: {
    count: 0
  },

  inviteRoles: [],

  exportFetching: false,
  exportResult: null,

  saveFetching: false,
  saveResult: null,

  resendResult: null,

  deleteFetching: false,
  deleteResult: null,

  filters: {
    status: "all",
    admin_full_name: [],
    email: [],
    exclude_fields: [],
    invite_sent: null,
    last_2fa_access: null,
    last_activity: null,
    role: "all"
  },
  visibleFilters: {
    status: true,
    admin_full_name: true,
    email: true,
    invite_sent: false,
    last_2fa_access: false,
    last_activity: false,
    role: true
  },

  defaultColumnsAdmin: [{ field_id: "status", label: "Status" }, ...columnsAdmin],
  columnsAdmin: [...columnsAdmin]
})

/* ------------- Selectors ------------- */

export const selectAreFiltersDefault = (state) =>
  isEqual(state.adminUsers.filters, INITIAL_STATE.filters)

/* ------------- Reducers ------------- */

const getAdminUsersRequest = (state) => state.merge({ fetching: true })
const getAdminUsersSuccess = (state, { payload: result }) =>
  state.merge({ fetching: false, result })
const getAdminUsersFailure = (state) => state.merge({ fetching: false })

const getAdminUsersCountRequest = (state) => state
const getAdminUsersCountSuccess = (state, { payload: statistics }) => state.merge({ statistics })
const getAdminUsersCountFailure = (state) => state

const getAdminUsersInviteRolesRequest = (state) => state
const getAdminUsersInviteRolesSuccess = (state, { payload: inviteRoles }) =>
  state.merge({ inviteRoles })
const getAdminUsersInviteRolesFailure = (state) => state

const getAdminUsersExportRequest = (state) => state.merge({ exportFetching: true })
const getAdminUsersExportSuccess = (state, { payload: exportResult }) =>
  state.merge({ exportFetching: false, exportResult })
const getAdminUsersExportFailure = (state) => state.merge({ exportFetching: false })

const updateAdminUserRoleRequest = (state) => state.merge({ saveFetching: true, saveResult: null })
const updateAdminUserRoleSuccess = (state, { payload: data }) =>
  state.merge({ saveFetching: false, saveResult: data || true })
const updateAdminUserRoleFailure = (state) => state.merge({ saveFetching: false })

const inviteAdminUserRequest = (state) => state.merge({ saveFetching: true, saveResult: null })
const inviteAdminUserSuccess = (state, { payload: data }) =>
  state.merge({ saveFetching: false, saveResult: data || true })
const inviteAdminUserFailure = (state) => state.merge({ saveFetching: false })

const resendAdminUserInviteRequest = (state) => state.merge({ resendResult: null })
const resendAdminUserInviteSuccess = (state, { payload: data }) =>
  state.merge({ resendResult: data || true })
const resendAdminUserInviteFailure = (state) => state

const deleteAdminUserRequest = (state) => state.merge({ deleteFetching: true, deleteResult: null })
const deleteAdminUserSuccess = (state, { payload: data }) =>
  state.merge({ deleteFetching: false, deleteResult: data || true })
const deleteAdminUserFailure = (state) => state.merge({ deleteFetching: false })

const adminUsersSetProp = (state, { key, value }) => {
  if (["filters", "visibleFilters"].includes(key)) {
    return state.merge({ [key]: { ...state[key], ...value } })
  }
  return state.merge({ [key]: value })
}

const clearAdminUsersFilters = (state) => state.merge({ filters: INITIAL_STATE.filters })

const logout = (state) => state.merge({ ...INITIAL_STATE })

// Bump when the shape of the saved `{ version, filters, columnsAdmin, visibleFilters }`
// payload changes.
export const ADMIN_USERS_PREFERENCES_VERSION = 1

const applyPreferences = (state, { payload }) => {
  const preference = payload?.["admin-users"]?.data
  if (!preference || preference.version !== ADMIN_USERS_PREFERENCES_VERSION) return state
  if (!preference.filters || !preference.columnsAdmin || !preference.visibleFilters) return state

  return state.merge({
    filters: preference.filters,
    columnsAdmin: preference.columnsAdmin,
    visibleFilters: preference.visibleFilters
  })
}

/* ------------- Hookup Reducers To Types ------------- */

export const reducer = createReducer(INITIAL_STATE, {
  [Types.GET_ADMIN_USERS_REQUEST]: getAdminUsersRequest,
  [Types.GET_ADMIN_USERS_SUCCESS]: getAdminUsersSuccess,
  [Types.GET_ADMIN_USERS_FAILURE]: getAdminUsersFailure,

  [Types.GET_ADMIN_USERS_COUNT_REQUEST]: getAdminUsersCountRequest,
  [Types.GET_ADMIN_USERS_COUNT_SUCCESS]: getAdminUsersCountSuccess,
  [Types.GET_ADMIN_USERS_COUNT_FAILURE]: getAdminUsersCountFailure,

  [Types.GET_ADMIN_USERS_INVITE_ROLES_REQUEST]: getAdminUsersInviteRolesRequest,
  [Types.GET_ADMIN_USERS_INVITE_ROLES_SUCCESS]: getAdminUsersInviteRolesSuccess,
  [Types.GET_ADMIN_USERS_INVITE_ROLES_FAILURE]: getAdminUsersInviteRolesFailure,

  [Types.GET_ADMIN_USERS_EXPORT_REQUEST]: getAdminUsersExportRequest,
  [Types.GET_ADMIN_USERS_EXPORT_SUCCESS]: getAdminUsersExportSuccess,
  [Types.GET_ADMIN_USERS_EXPORT_FAILURE]: getAdminUsersExportFailure,

  [Types.UPDATE_ADMIN_USER_ROLE_REQUEST]: updateAdminUserRoleRequest,
  [Types.UPDATE_ADMIN_USER_ROLE_SUCCESS]: updateAdminUserRoleSuccess,
  [Types.UPDATE_ADMIN_USER_ROLE_FAILURE]: updateAdminUserRoleFailure,

  [Types.INVITE_ADMIN_USER_REQUEST]: inviteAdminUserRequest,
  [Types.INVITE_ADMIN_USER_SUCCESS]: inviteAdminUserSuccess,
  [Types.INVITE_ADMIN_USER_FAILURE]: inviteAdminUserFailure,

  [Types.RESEND_ADMIN_USER_INVITE_REQUEST]: resendAdminUserInviteRequest,
  [Types.RESEND_ADMIN_USER_INVITE_SUCCESS]: resendAdminUserInviteSuccess,
  [Types.RESEND_ADMIN_USER_INVITE_FAILURE]: resendAdminUserInviteFailure,

  [Types.DELETE_ADMIN_USER_REQUEST]: deleteAdminUserRequest,
  [Types.DELETE_ADMIN_USER_SUCCESS]: deleteAdminUserSuccess,
  [Types.DELETE_ADMIN_USER_FAILURE]: deleteAdminUserFailure,

  [Types.ADMIN_USERS_SET_PROP]: adminUsersSetProp,
  [Types.CLEAR_ADMIN_USERS_FILTERS]: clearAdminUsersFilters,

  [Types.LOGOUT]: logout,

  [preferencesSuccess.type]: applyPreferences
})
